import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Truck,
  Package,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  ClipboardCheck,
  Loader2,
  Search,
  Thermometer,
  PackageCheck,
  Download,
  Mail,
  Upload
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const DeliveriesPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Mark this page as visited
  useMarkPageVisited('/deliveries');
  
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(null);

  const downloadReceptionPDF = async (order) => {
    setDownloading(order.id);
    try {
      const response = await api.get(`/orders/${order.id}/reception-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RECEPTION-${order.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloading(null);
    }
  };

  // Calculate discrepancies dynamically from order items
  const hasOrderDiscrepancies = (order) => {
    if (!order.items) return false;
    return order.items.some(item => {
      const qtyOrdered = item.quantity || 0;
      const qtyReceived = item.quantity_received || 0;
      const reliquat = qtyOrdered - qtyReceived;
      return reliquat > 0 || item.temperature_ok === false || item.packaging_ok === false || item.has_issue === true;
    });
  };

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    try {
      const [pendingRes, historyRes] = await Promise.all([
        api.get('/deliveries/pending'),
        api.get('/deliveries/history')
      ]);
      setPendingDeliveries(pendingRes.data);
      setDeliveryHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
    } finally {
      setLoading(false);
    }
  };


  const filteredPending = pendingDeliveries.filter(order =>
    order.order_number.toLowerCase().includes(search.toLowerCase()) ||
    order.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredHistory = deliveryHistory.filter(order =>
    order.order_number.toLowerCase().includes(search.toLowerCase()) ||
    order.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="deliveries-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Livraisons</h1>
        <p className="text-gray-500 mt-1">Réception et pointage des commandes</p>
        <p className="text-sm text-gray-600 mt-2 max-w-2xl">
          Réceptionnez vos livraisons et pointez les articles reçus. Validez les quantités et signalez les écarts éventuels.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Rechercher par n° commande ou fournisseur..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="deliveries-search-input"
        />
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            À réceptionner ({pendingDeliveries.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="space-y-4">
            {filteredPending.length === 0 ? (
              <Card className="border border-gray-200">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">Aucune livraison en attente</h3>
                  <p className="text-gray-500">Les commandes envoyées apparaîtront ici</p>
                </CardContent>
              </Card>
            ) : (
              filteredPending.map((order) => (
                <Card 
                  key={order.id} 
                  className="border border-gray-200 hover:border-[#1B4332]/30 transition-colors"
                  data-testid={`delivery-card-${order.order_number}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                          <Truck className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{order.order_number}</h3>
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
                              En attente
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 font-medium">{order.supplier_name}</p>
                          <p className="text-sm text-gray-500">
                            {order.items.length} article{order.items.length > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">
                            Livraison prévue: <strong>{format(new Date(order.delivery_date), 'd MMMM', { locale: fr })}</strong>
                          </span>
                        </div>

                        <Button 
                          onClick={() => navigate(`/deliveries/${order.id}/reception`)}
                          className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                          data-testid={`receive-${order.order_number}-btn`}
                        >
                          <ClipboardCheck className="w-4 h-4 mr-2" />
                          Pointer la réception
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <div className="space-y-4">
            {filteredHistory.length === 0 ? (
              <Card className="border border-gray-200">
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">Aucune livraison reçue</h3>
                  <p className="text-gray-500">L'historique des réceptions apparaîtra ici</p>
                </CardContent>
              </Card>
            ) : (
              filteredHistory.map((order) => (
                <Card 
                  key={order.id} 
                  className="border border-gray-200 hover:border-[#1B4332]/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/deliveries/${order.id}`)}
                  data-testid={`history-card-${order.order_number}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                          order.has_discrepancies ? 'bg-orange-100' : 'bg-green-100'
                        }`}>
                          {order.has_discrepancies ? (
                            <AlertTriangle className="w-6 h-6 text-orange-600" />
                          ) : (
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{order.order_number}</h3>
                            {order.has_discrepancies ? (
                              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">
                                Écarts signalés
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                                Conforme
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 font-medium">{order.supplier_name}</p>
                          <p className="text-sm text-gray-500">
                            Reçue le {format(new Date(order.received_at), 'd MMM à HH:mm', { locale: fr })}
                            {order.received_by && ` par ${order.received_by}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/deliveries/${order.id}`);
                          }}
                          data-testid={`view-reception-${order.order_number}-btn`}
                        >
                          <ClipboardCheck className="w-4 h-4 mr-1" />
                          Voir détail
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadReceptionPDF(order);
                          }}
                          disabled={downloading === order.id}
                          data-testid={`download-reception-${order.order_number}-btn`}
                        >
                          {downloading === order.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-1" />
                          )}
                          PDF
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default DeliveriesPage;
